import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { 
    ArrowRight, Users, Building2, Leaf, Calendar, 
    UtensilsCrossed, Heart, Sparkles, MapPin, ChevronRight,
    Phone, Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';

export function LandingPage() {
    const navigate = useNavigate();
    const { organization, loading } = useOrganization();
    const { isAuthenticated, user } = useAuth();

    // If already authenticated, redirect to appropriate dashboard
    useEffect(() => {
        if (isAuthenticated && user) {
            if (user.role === 'super_admin') {
                navigate('/super-admin');
            } else if (user.role === 'admin') {
                navigate('/admin');
            } else if (user.role === 'manager') {
                navigate('/manager');
            } else {
                // All members go to resident home (shopper dashboard paused)
                navigate('/home');
            }
        }
    }, [isAuthenticated, user, navigate]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-stone-50">
                <div className="w-10 h-10 border-4 border-stone-300 border-t-stone-600 rounded-full animate-spin" />
            </div>
        );
    }

    const features = [
        {
            icon: Calendar,
            title: 'Events & Activities',
            description: 'Discover talks, workshops, fitness classes, and social gatherings tailored for you.'
        },
        {
            icon: UtensilsCrossed,
            title: 'Dining & Hospitality',
            description: 'Restaurant menus, table reservations, and exclusive dining experiences.'
        },
        {
            icon: Heart,
            title: 'Wellness & Care',
            description: 'Yoga, fitness, spa services, and wellbeing support when you need it.'
        },
        {
            icon: Sparkles,
            title: 'Services & Concierge',
            description: 'Housekeeping, maintenance, transport, and personal appointments.'
        },
        {
            icon: Users,
            title: 'Community',
            description: 'Connect with neighbours, join clubs, and stay informed with announcements.'
        },
        {
            icon: MapPin,
            title: 'Local Area',
            description: 'Explore nearby attractions, partner experiences, and seasonal recommendations.'
        }
    ];

    return (
        <div className="min-h-screen bg-stone-50" data-testid="landing-page">
            {/* Hero Section */}
            <div className="relative min-h-[85vh] flex items-center">
                {/* Background Image with Overlay */}
                <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: 'url(https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1920&q=80)',
                    }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-stone-900/80 via-stone-900/60 to-transparent" />
                </div>

                {/* Navigation */}
                <nav className="absolute top-0 left-0 right-0 z-20 px-6 py-6">
                    <div className="max-w-7xl mx-auto flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {organization?.branding?.logo_url ? (
                                <img 
                                    src={organization.branding.logo_url} 
                                    alt={organization.name}
                                    className="h-12 w-auto"
                                />
                            ) : (
                                <div className="flex items-center gap-2">
                                    <Building2 className="w-8 h-8 text-white" />
                                    <span className="text-2xl font-serif text-white">
                                        {organization?.name || 'Residents Plus'}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-4">
                            <Button
                                variant="ghost"
                                onClick={() => navigate('/login')}
                                className="text-white hover:bg-white/20"
                                data-testid="login-btn"
                            >
                                Sign In
                            </Button>
                            <Button
                                onClick={() => navigate('/register')}
                                className="bg-white text-stone-900 hover:bg-stone-100"
                                data-testid="register-btn"
                            >
                                Register
                            </Button>
                        </div>
                    </div>
                </nav>

                {/* Hero Content */}
                <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
                    <div className="max-w-2xl">
                        <p className="text-amber-400 font-medium tracking-wider uppercase mb-4">
                            {organization?.tagline || 'Premium Lifestyle Membership'}
                        </p>
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-white leading-tight mb-6">
                            Welcome to<br />
                            <span className="text-amber-200">{organization?.name || 'Your Residence'}</span>
                        </h1>
                        <p className="text-xl text-stone-200 leading-relaxed mb-8">
                            Your complete guide to life at {organization?.name || 'your residence'}. 
                            Discover events, dining, wellness, and everything your community has to offer.
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <Button
                                size="lg"
                                onClick={() => navigate('/login')}
                                className="bg-amber-600 hover:bg-amber-700 text-white px-8 h-14 text-lg"
                                data-testid="hero-signin-btn"
                            >
                                Resident Sign In
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </Button>
                            <Button
                                size="lg"
                                variant="outline"
                                onClick={() => navigate('/register')}
                                className="border-white text-white hover:bg-white/10 px-8 h-14 text-lg"
                            >
                                Create Account
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Scroll Indicator */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
                    <div className="w-8 h-12 rounded-full border-2 border-white/40 flex items-start justify-center p-2">
                        <div className="w-1.5 h-3 bg-white/60 rounded-full animate-bounce" />
                    </div>
                </div>
            </div>

            {/* Features Section */}
            <section className="py-20 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-serif text-stone-800 mb-4">
                            Everything at Your Fingertips
                        </h2>
                        <p className="text-lg text-stone-600 max-w-2xl mx-auto">
                            One app for your entire lifestyle. From booking activities to ordering dinner, 
                            from wellness appointments to community updates.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {features.map((feature, index) => (
                            <div 
                                key={index}
                                className="group p-8 rounded-2xl border border-stone-200 hover:border-amber-300 hover:shadow-lg transition-all duration-300 bg-white"
                            >
                                <div className="w-14 h-14 rounded-xl bg-amber-50 flex items-center justify-center mb-6 group-hover:bg-amber-100 transition-colors">
                                    <feature.icon className="w-7 h-7 text-amber-700" />
                                </div>
                                <h3 className="text-xl font-semibold text-stone-800 mb-3">
                                    {feature.title}
                                </h3>
                                <p className="text-stone-600 leading-relaxed">
                                    {feature.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-6 bg-stone-800">
                <div className="max-w-4xl mx-auto text-center">
                    <Leaf className="w-12 h-12 text-amber-400 mx-auto mb-6" />
                    <h2 className="text-3xl md:text-4xl font-serif text-white mb-6">
                        Ready to explore?
                    </h2>
                    <p className="text-lg text-stone-300 mb-8 max-w-2xl mx-auto">
                        Sign in to access your personalised resident portal, or contact reception 
                        if you need assistance setting up your account.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Button
                            size="lg"
                            onClick={() => navigate('/login')}
                            className="bg-amber-600 hover:bg-amber-700 text-white px-8 h-14"
                        >
                            Sign In Now
                            <ChevronRight className="ml-2 w-5 h-5" />
                        </Button>
                    </div>
                </div>
            </section>

            {/* Contact Footer */}
            <footer className="py-12 px-6 bg-stone-900">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-4 gap-8">
                        <div>
                            <h3 className="text-lg font-semibold text-white mb-4">
                                {organization?.name || 'Residents Plus'}
                            </h3>
                            <p className="text-stone-400 text-sm">
                                {organization?.contact?.address || 'Your premier retirement community'}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-white mb-4">Contact</h3>
                            <div className="space-y-2 text-stone-400 text-sm">
                                {organization?.contact?.phone && (
                                    <p className="flex items-center gap-2">
                                        <Phone className="w-4 h-4" />
                                        {organization.contact.phone}
                                    </p>
                                )}
                                {organization?.contact?.email && (
                                    <p className="flex items-center gap-2">
                                        <Mail className="w-4 h-4" />
                                        {organization.contact.email}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-white mb-4">Quick Links</h3>
                            <div className="space-y-2 text-sm">
                                <Link to="/login" className="block text-stone-400 hover:text-amber-400 transition-colors">
                                    Resident Sign In
                                </Link>
                                <Link to="/register" className="block text-stone-400 hover:text-amber-400 transition-colors">
                                    Create Account
                                </Link>
                            </div>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-white mb-4">About Canopy</h3>
                            <div className="space-y-2 text-sm">
                                <a 
                                    href="/canopy-info-pack.html" 
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block text-stone-400 hover:text-amber-400 transition-colors"
                                >
                                    Platform Overview
                                </a>
                                <a 
                                    href="/social-media-services.html" 
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block text-stone-400 hover:text-amber-400 transition-colors"
                                >
                                    Content Services
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className="border-t border-stone-800 mt-8 pt-8 text-center text-stone-500 text-sm">
                        <p>Powered by <a href="/canopy-info-pack.html" target="_blank" rel="noopener noreferrer" className="hover:text-amber-400 transition-colors">Canopy</a></p>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export default LandingPage;
